import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  FormControlLabel,
  TextField,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { PERMISSION_PAGES } from "../../utils/permissionPages";

const COLUMNS = [
  { key: "canCreate", label: "Create" },
  { key: "canRead", label: "View" },
  { key: "canUpdate", label: "Edit" },
  { key: "canDelete", label: "Delete" },
];

const emptyPageRow = (pageName) => ({
  pageName,
  canCreate: false,
  canRead: true,
  canUpdate: false,
  canDelete: false,
});

// name is only editable for a brand-new user type - names can't be changed
// once created (see UserTypesTab.js), so editing an existing type only ever
// touches its permissions. duplicateFrom pre-fills a new type's permissions
// from an existing one (the "Duplicate" action) without reusing its name.
const PermissionsEditorDialog = ({ open, onClose, userType, duplicateFrom, isNew, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [name, setName] = useState("");
  const [pages, setPages] = useState([]);
  const [canApplyCustomDiscount, setCanApplyCustomDiscount] = useState(false);
  const [canDownloadPdfReports, setCanDownloadPdfReports] = useState(false);
  const [canOverridePrice, setCanOverridePrice] = useState(false);

  useEffect(() => {
    if (!open) return;
    const source = isNew ? duplicateFrom : userType;
    setName(isNew ? "" : userType?.name || "");
    const existingByName = new Map(
      (source?.permissions?.pages || []).map((page) => [page.pageName, page])
    );
    // Every canonical page always shows a row, even for a user type saved
    // before that page existed - it just starts with no access.
    setPages(PERMISSION_PAGES.map((pageName) => existingByName.get(pageName) || emptyPageRow(pageName)));
    setCanApplyCustomDiscount(source?.permissions?.canApplyCustomDiscount || false);
    setCanDownloadPdfReports(source?.permissions?.canDownloadPdfReports || false);
    setCanOverridePrice(source?.permissions?.canOverridePrice || false);
  }, [open, userType, duplicateFrom, isNew]);

  const setCell = (pageName, columnKey, value) => {
    setPages((prev) =>
      prev.map((page) => (page.pageName === pageName ? { ...page, [columnKey]: value } : page))
    );
  };

  const isColumnFullyChecked = (columnKey) => pages.every((page) => page[columnKey]);

  const toggleColumn = (columnKey) => {
    const nextValue = !isColumnFullyChecked(columnKey);
    setPages((prev) => prev.map((page) => ({ ...page, [columnKey]: nextValue })));
  };

  // Replaces the old behavior where typing a name that happened to lowercase
  // to "admin" silently granted every permission - that was invisible and
  // easy to trigger by accident. This is an explicit, visible action instead.
  const grantFullAccess = () => {
    setPages((prev) =>
      prev.map((page) => ({ ...page, canCreate: true, canRead: true, canUpdate: true, canDelete: true }))
    );
    setCanApplyCustomDiscount(true);
    setCanDownloadPdfReports(true);
    setCanOverridePrice(true);
  };

  const handleSave = () => {
    if (isNew && !name.trim()) return;
    onSave({
      name: name.trim(),
      permissions: { pages, canApplyCustomDiscount, canDownloadPdfReports, canOverridePrice },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ style: { borderRadius: isMobile ? 0 : 16 } }}
    >
      <DialogTitle style={{ backgroundColor: "#ef7f1a", color: "#fff" }}>
        {isNew ? (duplicateFrom ? `Duplicate "${duplicateFrom.name}"` : "New User Type") : `Permissions for ${userType?.name}`}
      </DialogTitle>
      <DialogContent style={{ paddingTop: 20 }}>
        {isNew && (
          <TextField
            autoFocus
            label="User Type Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            helperText="Names can't be changed later - choose one that will still make sense in a year."
            style={{ marginBottom: 16, maxWidth: 420 }}
          />
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <Button size="small" onClick={grantFullAccess} style={{ color: "#ef7f1a", fontWeight: 600 }}>
            Grant Full Access
          </Button>
        </div>

        <div style={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sr No</TableCell>
                <TableCell>Page</TableCell>
                {COLUMNS.map((column) => (
                  <TableCell key={column.key} align="center">
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      {column.label}
                      <Checkbox
                        size="small"
                        checked={isColumnFullyChecked(column.key)}
                        onChange={() => toggleColumn(column.key)}
                        color="primary"
                        title={`Toggle ${column.label} for every page`}
                      />
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pages.map((page, index) => (
                <TableRow key={page.pageName}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{page.pageName}</TableCell>
                  {COLUMNS.map((column) => (
                    <TableCell key={column.key} align="center">
                      <Checkbox
                        checked={!!page[column.key]}
                        onChange={(e) => setCell(page.pageName, column.key, e.target.checked)}
                        color="primary"
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Typography variant="subtitle2" style={{ marginTop: 20, marginBottom: 4 }}>
          Additional Permissions
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={canApplyCustomDiscount}
              onChange={(e) => setCanApplyCustomDiscount(e.target.checked)}
              color="primary"
            />
          }
          label="Can Apply Custom Discount"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={canDownloadPdfReports}
              onChange={(e) => setCanDownloadPdfReports(e.target.checked)}
              color="primary"
            />
          }
          label="Can Download Daily Sales Reports"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={canOverridePrice}
              onChange={(e) => setCanOverridePrice(e.target.checked)}
              color="primary"
            />
          }
          label="Allow Price Overrides"
        />
      </DialogContent>
      <DialogActions style={{ padding: "12px 24px 20px" }}>
        <Button onClick={onClose} fullWidth={isMobile}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isNew && !name.trim()}
          fullWidth={isMobile}
          style={{ backgroundColor: "#ef7f1a", color: "#fff" }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionsEditorDialog;
